import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
  CategoryScale,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-date-fns";

ChartJS.register(
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
  CategoryScale,
  zoomPlugin
);

const vw = (value) => Math.round((window.innerWidth * value) / 100);

const ensureFont = (obj) => {
  if (!obj.font) obj.font = {};
};

const createConnectionPlugin = (ranges) => ({
  id: "connectionStatus",
  beforeDatasetDraw(chart) {
    if (!ranges?.length) return;

    const { ctx, chartArea, scales } = chart;
    const xScale = scales.x;
    if (!xScale) return;

    ctx.save();
    ctx.beginPath();
    ctx.rect(
      chartArea.left,
      chartArea.top,
      chartArea.right - chartArea.left,
      chartArea.bottom - chartArea.top
    );
    ctx.clip();

    ctx.fillStyle = "rgba(239, 68, 68, 0.2)";

    ranges.forEach(({ start, end }) => {
      const xStart = xScale.getPixelForValue(start);
      const xEnd = xScale.getPixelForValue(end);

      if (xEnd > xStart) {
        ctx.fillRect(
          xStart,
          chartArea.top,
          xEnd - xStart,
          chartArea.bottom - chartArea.top
        );
      }
    });

    ctx.restore();
  },
});

export default function ChartBolt({ data, visible, translate }) {
  const t = typeof translate === "function" ? translate : (key) => key;

  const parsedData = useMemo(() => {
    if (!data?.length) return [];
    return data.map((item) => ({
      ...item,
      TS: new Date(item.TS),
    }));
  }, [data]);

  const disconnectionRanges = useMemo(() => {
    if (!parsedData.length) return [];

    const ranges = [];
    let start = null;

    parsedData.forEach((item, index) => {
      if (item.connection_status === false && start === null) {
        start = item.TS;
      }

      const isLast = index === parsedData.length - 1;
      const nextIsConnected =
        !isLast && parsedData[index + 1].connection_status !== false;

      if ((nextIsConnected || isLast) && start !== null) {
        const end = parsedData[index].TS;
        ranges.push({
          start,
          end: end > start ? end : new Date(start.getTime() + 1000),
        });
        start = null;
      }
    });

    return ranges;
  }, [parsedData]);

  const connectionPlugin = useMemo(
    () => createConnectionPlugin(disconnectionRanges),
    [disconnectionRanges]
  );

  if (!parsedData.length) return null;

  const chartData = {
    datasets: [
      {
        type: "bar",
        label: t("o2_instantaneous"),
        data: visible.flow_rate
          ? parsedData.map((item) => ({
              x: item.TS,
              y: item.flow_rate === -1 ? null : item.flow_rate,
            }))
          : [],
        backgroundColor: "rgba(96, 165, 250, 0.6)",
        yAxisID: "y1",
        borderWidth: vw(0.1),
        barThickness: "flex",
        maxBarThickness: 24,
      },
      {
        type: "line",
        label: t("o2_cumulative"),
        data: visible.cumulant
          ? parsedData.map((item) => ({
              x: item.TS,
              y: item.cumulant === -1 ? null : item.cumulant,
            }))
          : [],
        borderColor: "#34d399",
        backgroundColor: "rgba(52, 211, 153, 0.15)",
        yAxisID: "y",
        tension: 0.15,
        borderWidth: vw(0.08),
        pointRadius: 0,
        fill: true,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onResize(chart) {
      const fontSize = vw(0.9);
      const titleSize = vw(1);
      const { scales, plugins } = chart.options;

      ensureFont(scales.x.ticks);
      ensureFont(scales.y.ticks);
      ensureFont(scales.y1.ticks);
      ensureFont(scales.y.title);
      ensureFont(scales.y1.title);

      scales.x.ticks.font.size = fontSize;
      scales.y.ticks.font.size = fontSize;
      scales.y1.ticks.font.size = fontSize;
      scales.y.title.font.size = titleSize;
      scales.y1.title.font.size = titleSize;

      if (!plugins.tooltip.titleFont) plugins.tooltip.titleFont = {};
      if (!plugins.tooltip.bodyFont) plugins.tooltip.bodyFont = {};

      plugins.tooltip.titleFont.size = titleSize;
      plugins.tooltip.bodyFont.size = fontSize;
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (ctx) => {
            const ts = ctx[0].parsed.x;
            return new Date(ts).toLocaleString("es-CL", {
              hour12: false,
              hour: "2-digit",
              minute: "2-digit",
              day: "2-digit",
              month: "2-digit",
            });
          },
          afterBody: (ctx) => {
            const index = ctx[0].dataIndex;
            return data[index]?.connection_status === false
              ? t("no_communication")
              : t("connected");
          },
        },
      },
      zoom: {
        limits: {
          x: { min: "original", max: "original" },
        },
        pan: { enabled: true, mode: "x" },
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          mode: "x",
        },
      },
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit: "minute",
          displayFormats: { minute: "HH:mm" },
        },
        ticks: {
          color: "#9ca3af",
          maxTicksLimit: 6,
        },
        grid: { display: false },
      },
      y: {
        position: "right",
        ticks: { color: "#34d399" },
        grid: { color: "rgba(52, 211, 153, 0.15)" },
        title: {
          display: true,
          text: t("o2_cumulative"),
          color: "#34d399",
          font: { weight: "bold" },
        },
      },
      y1: {
        position: "left",
        ticks: { color: "#60a5fa" },
        grid: { drawOnChartArea: false },
        title: {
          display: true,
          text: t("o2_instantaneous"),
          color: "#60a5fa",
          font: { weight: "bold" },
        },
      },
    },
  };

  return (
    <Chart
      type="bar"
      data={chartData}
      options={options}
      plugins={[connectionPlugin]}
    />
  );
}
